import React, { useMemo } from "react";

import { Box, CardMedia, Container, IconButton } from "@material-ui/core";
import { Close } from "@material-ui/icons";

import MenuItemEditor, { MenuItemEditorProps } from "components/MenuItemEditor";
import makeStyles from "hooks/makeStyles";
import useCart from "hooks/useCart";
import useTranslation from "hooks/useTranslation";

const useStyles = makeStyles(theme => ({
	root: {
		alignItems: "center",
		display: "flex",
		flex: 1,
		flexDirection: "column",
		padding: 0,
		height: "calc(65vh - 95px)", // not great..
		position: "relative"
	},
	imageContainer: {
		width: "100%",
		height: "200px",
		position: "absolute",
		top: 0,
		left: 0,
		zIndex: 1
	},
	imageCard: {
		height: "100%",
		width: "100%"
	},
	imageContainerPlaceholder: {
		height: "200px",
		marginBottom: -theme.spacing(4),
		[theme.breakpoints.up(480)]: {
			marginBottom: -theme.spacing(7)
		}
	},
	closeButton: {
		marginLeft: -2,
		marginTop: -2,
		padding: 0,
		textAlign: "left",
		alignSelf: "flex-start",
		[theme.breakpoints.up(480)]: {
			padding: theme.spacing(1),
			margin: theme.spacing(1),
			color: theme.palette.text.primary
		}
	},
	closeIcon: {
		width: 16,
		height: 16,
		fill: "#fff",
		zIndex: 2,
		[theme.breakpoints.up(480)]: {
			fill: theme.palette.text.primary
		}
	}
}));

interface Props {
	cartItemLocalId: string;
	onClose: () => void;
}

const MenuItemModal = ({ cartItemLocalId, onClose }: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();

	const { cartItemByLocalId, updateCartItem, isItemAvailable } = useCart();

	const handleSubmit = useMemo<MenuItemEditorProps["onSubmit"]>(
		() => ({ state }) => {
			updateCartItem({ ...state, localId: cartItemLocalId });
			onClose();
		},
		[cartItemLocalId, onClose, updateCartItem]
	);

	const cartItem = cartItemByLocalId && cartItemByLocalId[cartItemLocalId];
	const menuItem = cartItem?.menuItem;

	if (!menuItem) {
		return null;
	}

	const itemAvailable = isItemAvailable(menuItem);

	return (
		<>
			{menuItem.imageUrl && (
				<Box className={classes.imageContainer}>
					<CardMedia image={menuItem.imageUrl} className={classes.imageCard}></CardMedia>
				</Box>
			)}
			<Container className={classes.root}>
				<IconButton className={classes.closeButton} onClick={onClose}>
					<Close className={classes.closeIcon} />
				</IconButton>
				{menuItem.imageUrl && <Box className={classes.imageContainerPlaceholder}></Box>}
				<MenuItemEditor
					menuItem={menuItem}
					itemAvailable={itemAvailable}
					defaultSpecialInstructions={cartItem?.specialInstructions}
					defaultQuantity={cartItem?.quantity}
					defaultSelectedOptions={cartItem?.selectedOptions}
					submitLabel={t("updateOrder")}
					onSubmit={handleSubmit}
				/>
			</Container>
		</>
	);
};

export default MenuItemModal;
